import {
  FetchUserAssessmentParams,
  fetchUserAssessmentRequest,
  submitUserAssessment,
  SubmitUserAssessmentPayload,
} from '@/requests';
import { AnalyticsService, EVENTS } from '@/services';
import { Assessment, SessionSummary } from '@/types';
import { camelCaseKeys } from '@/utils';

import { playerFsmApi } from '../player-fsm-api';
import { postgrestApi } from '../postgrest-api';
import { PostgrestApiTags } from '../store.constants';
import { handleOnQueryStartedError } from '../store.utils';
import { SubmitUserAssessmentResponse } from './assessments-api.types';

export const assessmentsApi = postgrestApi.injectEndpoints({
  endpoints: builder => ({
    fetchEpisodeAssessment: builder.query<
      Assessment & { sessionId?: string },
      FetchUserAssessmentParams
    >({
      query: fetchUserAssessmentRequest,
      transformResponse: (response: unknown[]) => camelCaseKeys(response),
      onQueryStarted: async (_, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled;

          const episodeId = data?.episodeId;
          if (!episodeId) return;

          const savedSession =
            playerFsmApi.endpoints.fetchPlayerSessions.select()(getState())
              ?.data?.entities[episodeId] as SessionSummary;

          if (!savedSession) {
            dispatch(
              postgrestApi.util.invalidateTags([
                PostgrestApiTags.PLAYER_SESSIONS,
              ]),
            );
          }
        } catch (error) {
          handleOnQueryStartedError(error);
        }
      },
      providesTags: [PostgrestApiTags.EPISODE_ASSESSMENT],
    }),
    submitUserAssessment: builder.mutation<
      SubmitUserAssessmentResponse,
      SubmitUserAssessmentPayload
    >({
      query: submitUserAssessment,
      extraOptions: {
        withLock: true,
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          AnalyticsService.logEvent(EVENTS.ASSESSMENT_EVENTS.SUBMIT.INTENT);
          await queryFulfilled;
          AnalyticsService.logEvent(EVENTS.ASSESSMENT_EVENTS.SUBMIT.SUCCESS);
        } catch (error: any) {
          AnalyticsService.logEvent(EVENTS.ASSESSMENT_EVENTS.SUBMIT.FAIL, {
            error: error?.message,
          });
          handleOnQueryStartedError(error);
        }
      },
      transformResponse: (response: any) =>
        camelCaseKeys({
          ...response,
          score: Math.round(response.score * 100) / 100,
        }),
      invalidatesTags: (resp, err, arg) => [
        PostgrestApiTags.PLAYER_SESSIONS,
        PostgrestApiTags.EPISODE_ASSESSMENT,
        {
          type: PostgrestApiTags.PLAYER_SESSION_DETAILS,
          id: arg?.episodeId,
        },
      ],
    }),
  }),
});

export const {
  useFetchEpisodeAssessmentQuery,
  useSubmitUserAssessmentMutation,
} = assessmentsApi;
